import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { 
  StyledLanguageSwitcher, 
  StyledLanguageButton, 
  StyledLanguageDropdown, 
  StyledLanguageOption 
} from './LanguageSwitcher.styles';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱' },
];

export const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
    
    // Store preference in localStorage
    localStorage.setItem('selectedLanguage', languageCode);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <StyledLanguageSwitcher 
      ref={dropdownRef} 
      onKeyDown={handleKeyDown}
      className="language-switcher-container"
    >
      <StyledLanguageButton
        onClick={handleToggle}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe size={16} />
        <span className="language-text">
          <span className="language-flag">{currentLanguage.flag}</span>
          <span className="language-name">{currentLanguage.nativeName}</span>
        </span>
      </StyledLanguageButton>

      <StyledLanguageDropdown 
        $isOpen={isOpen} 
        role="listbox" 
        aria-label="Language options"
        className="language-dropdown"
      >
        {languages.map((language) => (
          <StyledLanguageOption
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={`language-option ${language.code === i18n.language ? 'active' : ''}`}
            role="option"
            aria-selected={language.code === i18n.language}
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleLanguageChange(language.code);
              }
            }}
          >
            <span className="flag">{language.flag}</span>
            <div className="language-info">
              <span className="native-name">{language.nativeName}</span>
              <span className="english-name">{language.name}</span>
            </div>
          </StyledLanguageOption>
        ))}
      </StyledLanguageDropdown>
    </StyledLanguageSwitcher>
  );
};

export default LanguageSwitcher;
